/* ============================================================
   OrbScene — brings the orb scene to life.

   It does two things, and draws nothing itself:

   1. THE AWAKENING. A few seconds after the section comes into view, the
      orb and the lines slowly gain energy: the orb gets brighter and whiter
      with a stronger glow, more lights run along the lines, faster, and
      whole strands begin to light up. It then stays awake, breathing gently.

   2. THE BUBBLES. Once it is awake, small labels ("Pattern noticed",
      "Core Insight" …) appear on random nodes of the web, stay a few
      seconds and fade. Each one is fixed by its dot to a dot of the web, which
      lights up while the label is there, and drifts with it. They never cover the orb, each other or the edge.

   Needs light-orb.js and neural-lines.js on the same page.

   USE
     Put  data-orb-scene  on the section that holds the two canvases, and
     load this file after the other two. The bubbles are ordinary HTML, so
     their look is plain CSS (.orb-scene__bubble, see index.html).

     Options as attributes on the section:
       data-wake="3"      seconds in view before it starts to wake
       data-rise="12"     seconds it takes to reach full energy
       data-labels="Pattern noticed|Core Insight"     the texts, separated by |
       data-every="4.2"   seconds between new bubbles (roughly)
       data-hold="3.8"    seconds a bubble stays
       data-max="2"       how many may show at once
   ============================================================ */
(function (global) {
  'use strict';

  var DEFAULTS = {
    wake: 3,
    rise: 12,
    labels: 'Pattern noticed|Core Insight',
    every: 4.2,
    hold: 3.8,
    max: 2
  };

  function ease(t) { t = Math.min(1, Math.max(0, t)); return t * t * (3 - 2 * t); }

  function OrbScene(section, options) {
    var cfg = {}, key;
    for (key in DEFAULTS) cfg[key] = options && options[key] !== undefined ? options[key] : DEFAULTS[key];
    var labels = String(cfg.labels).split('|').map(function (s) { return s.trim(); }).filter(Boolean);
    var orbEl = section.querySelector('canvas[data-light-orb]'), linesEl = section.querySelector('canvas[data-neural-lines]');
    var still = global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var layer = section.querySelector('.orb-scene__bubbles');
    if (!layer) { layer = document.createElement('div'); layer.className = 'orb-scene__bubbles'; layer.setAttribute('aria-hidden', 'true'); section.appendChild(layer); }

    var clock = 0, energy = 0, bubbles = [], nextAt = 0, lastLabel = -1, forced = null;

    function apply(e) {
      energy = e;
      if (orbEl && orbEl.__orb) orbEl.__orb.set('energy', e);
      if (linesEl && linesEl.__lines) linesEl.__lines.set('energy', e);
      section.style.setProperty('--energy', e.toFixed(3));
    }

    // Canvas coordinates are viewport pixels; the theme uses body zoom.
    function zoom() { return section.getBoundingClientRect().width / section.offsetWidth || 1; }
    function rect(el) {
      var r = el.getBoundingClientRect(), z = zoom();
      return {left:r.left/z, top:r.top/z, width:r.width/z, height:r.height/z};
    }
    function localNodes(lines) {
      var z = zoom();
      return lines.nodes().map(function(n) { return Object.assign({}, n, {x:n.x/z, y:n.y/z}); });
    }
    /* ---------- bubbles ---------- */
    function rectHitsCircle(r, cx, cy, rad) {
      var x = Math.max(r.l, Math.min(cx, r.r)), y = Math.max(r.t, Math.min(cy, r.b));
      return (x - cx) * (x - cx) + (y - cy) * (y - cy) < rad * rad;
    }
    function spawn() {
      var lines = linesEl && linesEl.__lines; if (!lines || !labels.length) return false;
      var nodes = localNodes(lines), L = rect(layer), C = rect(linesEl);
      var ox = C.left - L.left, oy = C.top - L.top;

      var pick = Math.floor(Math.random() * labels.length);
      if (labels.length > 1 && pick === lastLabel) pick = (pick + 1) % labels.length;

      var el = document.createElement('div'); el.className = 'orb-scene__bubble';
      el.innerHTML = '<div class="orb-scene__pill"><i></i><span></span></div>';
      el.querySelector('span').textContent = labels[pick];
      layer.appendChild(el);
      var pill = el.firstChild, dot = pill.querySelector('i');
      var ax = dot.offsetLeft + dot.offsetWidth / 2, ay = pill.offsetHeight / 2, w = pill.offsetWidth, h = pill.offsetHeight;   // the dot sits on the node

      var orb = null;
      if (orbEl) { var o = rect(orbEl); orb = { x: o.left + o.width / 2 - L.left, y: o.top + o.height / 2 - L.top, r: Math.min(o.width, o.height) * 0.5 * 0.62 }; }

      var ok = nodes.filter(function (n) {
        if (n.kind === 'soft' || n.visibility < 0.6) return false;      // only on clear, sharp dots of the web
        if (bubbles.some(function (b) { return b.node === n.index; })) return false;
        var r = { l: n.x + ox - ax - 10, t: n.y + oy - ay - 10, r: n.x + ox - ax + w + 10, b: n.y + oy - ay + h + 10 };
        if (r.l < 6 || r.t < 6 || r.r > L.width - 6 || r.b > L.height - 6) return false;
        if (orb && rectHitsCircle(r, orb.x, orb.y, orb.r)) return false;
        return !bubbles.some(function (b) { return b.rect && r.l < b.rect.r && r.r > b.rect.l && r.t < b.rect.b && r.b > b.rect.t; });
      });
      if (!ok.length) { layer.removeChild(el); return false; }
      ok.sort(function (p, q) { return q.links - p.links; });          // prefer the busy dots, where many lines meet
      var n = ok[Math.floor(Math.random() * Math.min(ok.length, Math.max(3, Math.ceil(ok.length / 2))))];
      lastLabel = pick;
      var b = { el: el, node: n.index, ax: ax, ay: ay, w: w, h: h, until: clock + cfg.hold * (0.85 + Math.random() * 0.4), gone: 0, rect: null };
      bubbles.push(b); place(b, n, ox, oy);
      lines.highlight(n.index, 1);                                    // the dot it sits on lights up and stays lit
      el.offsetWidth;                                                 // so the entrance transition runs
      el.classList.add('is-on');
      return true;
    }
    function place(b, n, ox, oy) {
      var x = n.x + ox - b.ax, y = n.y + oy - b.ay;
      b.el.style.transform = 'translate3d(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px,0)';
      b.rect = { l: x - 10, t: y - 10, r: x + b.w + 10, b: y + b.h + 10 };
    }
    function updateBubbles() {
      var lines = linesEl && linesEl.__lines; if (!lines) return;
      if (bubbles.length) {
        var nodes = localNodes(lines), L = rect(layer), C = rect(linesEl), ox = C.left - L.left, oy = C.top - L.top, i, b;
        for (i = bubbles.length - 1; i >= 0; i--) {
          b = bubbles[i];
          if (nodes[b.node]) place(b, nodes[b.node], ox, oy);
          if (!b.gone && clock > b.until) { b.gone = clock; b.el.classList.remove('is-on'); lines.highlight(b.node, 0); }
          if (b.gone && clock > b.gone + 1.1) { layer.removeChild(b.el); bubbles.splice(i, 1); }
        }
      }
      var showing = bubbles.filter(function (b) { return !b.gone; }).length;
      if (energy > 0.35 && clock >= nextAt && showing < cfg.max) {
        nextAt = clock + (spawn() ? cfg.every * (0.7 + Math.random() * 0.6) : 0.8);
      }
    }

    /* ---------- running ---------- */
    var raf = 0, visible = false, last = 0, dead = false;
    function frame(now) {
      raf = 0;
      if (!visible || dead) return;
      var dt = last ? Math.min((now - last) / 1000, 0.05) : 0.016; last = now;
      clock += dt;
      if (forced === null) {
        var e = ease((clock - cfg.wake) / Math.max(cfg.rise, 0.1));
        apply(e * (0.94 + 0.06 * Math.sin(clock * 0.55)));           // awake, it breathes a little
      }
      updateBubbles();
      raf = requestAnimationFrame(frame);
    }
    function wake() { if (!raf && visible && !dead) { last = 0; raf = requestAnimationFrame(frame); } }

    if (still) {                                                      // reduced motion: a calm, lit still; no bubbles popping
      var tries = 0, t = setInterval(function () { apply(0.6); if (++tries > 20 || (orbEl && orbEl.__orb && linesEl && linesEl.__lines)) clearInterval(t); }, 100);
    } else if (global.IntersectionObserver) {
      var io = new IntersectionObserver(function (en) { visible = en[en.length - 1].isIntersecting; wake(); }, { threshold: 0.2 });
      io.observe(section);
    } else { visible = true; wake(); }

    return {
      /* for testing: jump the scene's own clock forward, or pin the energy by hand (null = automatic again) */
      advance: function (seconds) { clock += seconds; if (forced === null) apply(ease((clock - cfg.wake) / Math.max(cfg.rise, 0.1)) * (0.94 + 0.06 * Math.sin(clock * 0.55))); updateBubbles(); },
      setEnergy: function (e) { forced = e; if (e !== null) apply(e); },
      destroy: function () { dead = true; if (raf) cancelAnimationFrame(raf); if (io) io.disconnect(); bubbles.forEach(function (b) { layer.removeChild(b.el); }); bubbles = []; }
    };
  }

  function auto(root) {
    root = root && root.querySelectorAll ? root : document;
    var list = root.querySelectorAll('[data-orb-scene]');
    for (var i = 0; i < list.length; i++) {
      var el = list[i], opts = {}, key;
      if (el.__scene) continue;
      for (key in DEFAULTS) {
        var raw = el.dataset[key];
        if (raw === undefined) continue;
        opts[key] = typeof DEFAULTS[key] === 'number' ? parseFloat(raw) : raw;
      }
      el.__scene = OrbScene(el, opts);
    }
  }

  global.OrbScene = OrbScene;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', auto);
  else auto();
  document.addEventListener('shopify:section:load', function (e) { auto(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('[data-orb-scene]').forEach(function (el) {
      if (el.__scene) { el.__scene.destroy(); delete el.__scene; }
    });
  });
})(window);

